import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import toast from 'react-hot-toast'
import { apiFetch } from '../lib/api'

export interface DividendRecord {
  id: number
  symbol: string
  name: string
  port_api_type: 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold'
  amount_per_share: number
  currency: 'USD' | 'THB'
  ex_date: string
  pay_date?: string | null
  shares_held: number
  total_received: number
  notes?: string | null
  created_at: string
  updated_at: string
}

export type DividendInput = Omit<DividendRecord, 'id' | 'created_at' | 'updated_at'>

interface DividendState {
  dividends: DividendRecord[]
  loading: boolean
  loaded: boolean
  loadDividends: (force?: boolean) => Promise<void>
  addDividend: (data: DividendInput) => Promise<void>
  updateDividend: (id: number, data: Partial<DividendInput>) => Promise<void>
  deleteDividend: (id: number) => Promise<void>
}

export const useDividendStore = create<DividendState>()(
  devtools(
    (set, get) => ({
      dividends: [],
      loading: false,
      loaded: false,

      loadDividends: async (force = false) => {
        const { loading, loaded } = get()
        if (!force && (loaded || loading)) return
        set({ loading: true })
        try {
          const res = await apiFetch('/api/dividends')
          if (!res.ok) throw new Error('Failed to load dividends')
          set({ dividends: await res.json(), loaded: true })
        } catch {
          toast.error('Failed to load dividends')
        } finally {
          set({ loading: false })
        }
      },

      addDividend: async (data) => {
        try {
          const res = await apiFetch('/api/dividends', { method: 'POST', body: JSON.stringify(data) })
          if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
          const created = await res.json()
          set(s => ({ dividends: [created, ...s.dividends] }))
          toast.success('บันทึกข้อมูลเงินปันผลแล้ว')
        } catch (err: unknown) {
          toast.error(err instanceof Error ? err.message : 'Failed to add dividend')
          throw err
        }
      },

      updateDividend: async (id, data) => {
        try {
          const res = await apiFetch(`/api/dividends/${id}`, { method: 'PUT', body: JSON.stringify(data) })
          if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
          const updated = await res.json()
          set(s => ({ dividends: s.dividends.map(d => d.id === id ? updated : d) }))
          toast.success('อัพเดทข้อมูลแล้ว')
        } catch (err: unknown) {
          toast.error(err instanceof Error ? err.message : 'Failed to update dividend')
          throw err
        }
      },

      deleteDividend: async (id) => {
        try {
          const res = await apiFetch(`/api/dividends/${id}`, { method: 'DELETE' })
          if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
          set(s => ({ dividends: s.dividends.filter(d => d.id !== id) }))
          toast.success('ลบรายการแล้ว')
        } catch (err: unknown) {
          toast.error(err instanceof Error ? err.message : 'Failed to delete dividend')
          throw err
        }
      },
    }),
    { name: 'dividendStore' }
  )
)
